// ═══════════════════════════════════════════════════════════════════════════
// promoCanonicoShape — señal de promo + costo de ads desde la canónica (C6b)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mappers PUROS para reconectar Contribución Neta + Velocidad de Consumo a la
// canónica (detrás de flag por-lector), reemplazando las lecturas legacy:
//   - señal "en promo" ← `publicaciones.facetas.enPromo` (C4b)  (antes:
//     cruce con `mlPublicacionesPromos`)
//   - costo de ads por ítem ← `campanasMetricas` refTipo='publicacion' (C5b)
//     (antes: `mlAds` — colección CONGELADA desde que murió syncMLAds)
//   - set de ítems con ad ACTIVO ← `campanasItems` subtipo='ads' estado='active'
//     (antes: `mlAds` status='active')
//
// ⚠️ DIVERGENCIA DECLARADA (regla de consistencia de números): `facetas.enPromo`
// cuenta SOLO promos `started`; el cruce legacy `promoIds` contaba CUALQUIER
// participación (candidatas incluidas). Migrar ENDURECE la señal "en promo"
// (una candidata no descuenta el precio hoy). Medir el delta con
// `verificar-facetas-promo.cjs` y re-baselinear tests ANTES de prender el flag.
//
// PUROS — sin React, sin Firestore. Golden-testeables.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Señal "en promo" en el shape legacy `dealIds` (los consumidores solo miran
 * `.length > 0`). Fuente: `publicaciones.facetas.enPromo` (top-level, C4b).
 *
 * @param {Object} pub — doc canónico de publicaciones
 * @returns {Array<string>} — ['promo-activa'] o []
 */
export function dealIdsDesdeFaceta(pub) {
  return pub?.facetas?.enPromo === true ? ['promo-activa'] : []
}

/**
 * Costo de ads por ítem desde `campanasMetricas` refTipo='publicacion', en el
 * shape que el prorrateo de Contribución consume (`ad.metrics || ad` → cost /
 * units_quantity, top-level).
 *
 * Nota: `metricas` puede venir sin `units_quantity` (ML omite claves sin valor;
 * mapearMetricas no las pone en 0). En ese caso queda 0 y el prorrateo descarta
 * ese ad (units<=0 → continue) — mismo comportamiento que el legacy mlAds con
 * units 0. No se inventa atribución sin unidades.
 *
 * @param {Array<{refExternalId, metricas}>} metricasPubs
 * @returns {Array<{item_id, cost, units_quantity}>}
 */
export function adsLegacyDesdeMetricas(metricasPubs = []) {
  const out = []
  for (const m of metricasPubs || []) {
    if (!m || m.refExternalId == null) continue
    const met = m.metricas || {}
    out.push({
      item_id: String(m.refExternalId),
      cost: Number(met.cost) || 0,
      units_quantity: Number(met.units_quantity) || 0,
    })
  }
  return out
}

// Estados que significan "el ad ya NO existe" (no cuentan como inscripto). El
// resto (active/paused/idle/hold/…) = inscripto. El `status` del ad en ML es
// volátil (ej: todos en 'hold' fuera de la ventana de spend), por eso NO se
// filtra por 'active' (daba 0): el C5a hace mark-and-sweep → un doc que EXISTE
// es un ad que ML todavía lista = el item tiene publicidad armada.
export const ESTADOS_AD_MUERTO = new Set(['deleted', 'stopped', 'removed', 'cancelled'])

/**
 * ¿El ad sigue existiendo en ML? (cualquier estado salvo los de muerto;
 * `hold`/`idle`/`paused` SON vivos — status intradía-volátil, ML-validado C6b).
 * Única semántica válida para "tiene publicidad" en dashboards Y clasificadores
 * (C6g): filtrar por `status === 'active'` contra la canónica da 0.
 */
export function esAdVivo(status) {
  return status == null || !ESTADOS_AD_MUERTO.has(String(status))
}

/**
 * Set de item_ids que tienen publicidad ARMADA (inscriptos en ads) desde
 * `campanasItems` subtipo='ads' — cualquier estado salvo los de ad muerto.
 *
 * @param {Array<Object>} campanasItemsAds — docs campanasItems (subtipo='ads')
 * @returns {Set<string>}
 */
export function itemsConAdDesdeCampanasItems(campanasItemsAds = []) {
  const set = new Set()
  for (const ci of campanasItemsAds || []) {
    if (!ci || ci.subtipo !== 'ads') continue
    if (ci.estado != null && ESTADOS_AD_MUERTO.has(String(ci.estado))) continue
    if (ci.publicacionExternalId != null) set.add(String(ci.publicacionExternalId))
  }
  return set
}
